/**
 * pwa.js — Service Worker registration + install prompt orchestration.
 * Imported once from src/index.js.
 *
 * IMPORTANT (2026-05-16 hotfix):
 *   The platform is being iterated on rapidly. Until the UI stabilises, we
 *   AGGRESSIVELY KILL any existing Service Worker + all caches on every page
 *   load. This guarantees users never see stale UI without needing
 *   Ctrl+Shift+R.
 *
 *   When the app is shipped to production we can re-enable the
 *   network-first SW by flipping `ENABLE_SW` to true.
 */

const ENABLE_SW = false;

export const registerPWA = () => {
  if (typeof window === 'undefined') return;

  // Capture install prompt for custom UI
  window.addEventListener('beforeinstallprompt', (e) => {
    e.preventDefault();
    window.__deferredInstallPrompt = e;
    window.dispatchEvent(new CustomEvent('pwa-installable'));
  });

  window.addEventListener('appinstalled', () => {
    window.__deferredInstallPrompt = null;
    window.dispatchEvent(new CustomEvent('pwa-installed'));
    try { localStorage.setItem('pwa_installed', '1'); } catch (_) { /* noop */ }
  });

  if (!('serviceWorker' in navigator)) return;
  // Skip SW in iframe-embedded preview (visual-edit) to avoid noisy 404s.
  if (window.self !== window.top) return;

  /* ───────── KILL SWITCH ─────────
     Unregister ANY active service worker, nuke ALL caches, and reload once
     so users on stale-cached builds immediately pick up fresh assets. */
  const killAllServiceWorkers = async () => {
    try {
      const regs = await navigator.serviceWorker.getRegistrations();
      const hadRegs = regs.length > 0;
      await Promise.all(regs.map((r) => r.unregister().catch(() => false)));

      if ('caches' in window) {
        const keys = await caches.keys();
        await Promise.all(keys.map((k) => caches.delete(k).catch(() => false)));
      }

      // If we found and removed at least one SW, reload ONCE so the page
      // re-fetches every asset directly from the network with no cache.
      // sessionStorage prevents an infinite reload loop.
      if (hadRegs && !sessionStorage.getItem('sw_kill_done')) {
        sessionStorage.setItem('sw_kill_done', '1');
        window.location.reload();
      }
    } catch (_) { /* silent */ }
  };

  window.addEventListener('load', () => {
    killAllServiceWorkers();

    if (!ENABLE_SW) return; // SW disabled during active dev.

    navigator.serviceWorker
      .register('/service-worker.js')
      .then((reg) => {
        if (reg && typeof reg.update === 'function') {
          window.addEventListener('focus', () => { reg.update().catch(() => {}); });
        }
      })
      .catch(() => { /* silent */ });

    navigator.serviceWorker.addEventListener('message', (event) => {
      if (event.data?.type === 'SW_UPDATED') {
        const last = sessionStorage.getItem('sw_reloaded_for');
        if (last !== event.data.version) {
          sessionStorage.setItem('sw_reloaded_for', event.data.version);
          window.location.reload();
        }
      }
    });

    let didControllerChange = false;
    navigator.serviceWorker.addEventListener('controllerchange', () => {
      if (didControllerChange) return;
      didControllerChange = true;
      window.location.reload();
    });
  });
};

export const triggerInstallPrompt = async () => {
  const ev = window.__deferredInstallPrompt;
  if (!ev) return { outcome: 'unavailable' };
  ev.prompt();
  try {
    const choice = await ev.userChoice;
    window.__deferredInstallPrompt = null;
    return choice;
  } catch (_) {
    window.__deferredInstallPrompt = null;
    return { outcome: 'dismissed' };
  }
};

export const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)')?.matches ||
    window.navigator?.standalone === true
  );
};
